"use strict";
/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { WatchableValue } from "#src/trackable_value.js";
import { RefCounted } from "#src/util/disposable.js";
import {
  dispatchEventWithModifiers,
  EventActionMap,
  registerActionListener,
  getEventModifierMask
} from "#src/util/event_action_map.js";
export const globalModifiers = new WatchableValue(0);
window.addEventListener("keydown", (event) => {
  globalModifiers.value = getEventModifierMask(event);
});
window.addEventListener("keyup", (event) => {
  globalModifiers.value = getEventModifierMask(event);
});
const globalKeys = /* @__PURE__ */ new Set([
  "f1",
  "f2",
  "f3",
  "f4",
  "f5",
  "f6",
  "f7",
  "f8",
  "f9",
  "f10",
  "f11",
  "f12",
  "escape",
  "pause"
]);
const DEFAULT_TEXT_INPUTS = /* @__PURE__ */ new Set([
  "color",
  "date",
  "datetime",
  "datetime-local",
  "email",
  "month",
  "number",
  "password",
  "search",
  "tel",
  "text",
  "time",
  "url",
  "week"
]);
export class KeyboardEventBinder extends RefCounted {
  constructor(target, eventMap) {
    super();
    this.target = target;
    this.eventMap = eventMap;
    this.modifierShortcutsAreGlobal = true;
    this.allShortcutsAreGlobal = false;
    this.allowSpaceKeyOnButtons = false;
    this.shouldIgnore = void 0;
    this.registerEventListener(
      target,
      "keydown",
      this.handleKeyDown.bind(this),
      /*useCapture=*/
      false
    );
  }
  shouldIgnoreEvent(key, event) {
    if (this.shouldIgnore?.(event)) return true;
    const el = event.target;
    const { tagName } = el;
    if (el === this.target) {
      return false;
    }
    const isFormElement = tagName === "TEXTAREA" || tagName === "INPUT" || tagName === "BUTTON" || tagName === "SELECT";
    const isContentEditable = !isFormElement && (el.isContentEditable || el.ownerDocument && el.ownerDocument.designMode === "on");
    if (!isFormElement && !isContentEditable) {
      return false;
    }
    if (this.allShortcutsAreGlobal || globalKeys.has(key)) {
      return false;
    }
    if (isContentEditable) {
      return true;
    }
    if (this.modifierShortcutsAreGlobal && (event.altKey || event.ctrlKey || event.metaKey)) {
      return true;
    }
    if (tagName === "INPUT" && DEFAULT_TEXT_INPUTS.has(el.type)) {
      return key !== "enter";
    }
    if (tagName === "INPUT" || tagName === "BUTTON") {
      if (this.allowSpaceKeyOnButtons) {
        return false;
      }
      return key === "space";
    }
    return true;
  }
  handleKeyDown(event) {
    const key = getEventKeyName(event);
    if (this.shouldIgnoreEvent(key, event)) {
      return;
    }
    dispatchEventWithModifiers(key, event, event, this.eventMap);
  }
}
export function getEventKeyName(event) {
  return event.code.toLowerCase();
}
export { EventActionMap, registerActionListener };
